import mediaUploadSpec from '../config/mediaUploadSpec.json';

export type UploadMediaType = 'video' | 'audio' | 'image' | 'unknown';

// This byte limit is also loaded by the backend. Do not add environment or
// per-surface overrides: local uploads and selected Drive files share it.
export const MAX_MEDIA_UPLOAD_BYTES = mediaUploadSpec.max_file_size_bytes;
export const MEDIA_UPLOAD_SIZE_LIMIT_LABEL = `${MAX_MEDIA_UPLOAD_BYTES / (1024 ** 3)} GB`;
export const MEDIA_UPLOAD_SIZE_LIMIT_MESSAGE = `Each file must be ${MEDIA_UPLOAD_SIZE_LIMIT_LABEL} or smaller.`;

/** Picker size is a display hint; the backend enforces the actual byte count. */
export const exceedsMediaUploadSizeLimit = (sizeBytes: number): boolean => (
  sizeBytes > MAX_MEDIA_UPLOAD_BYTES
);

// Match the minimum-plan processing burst. A larger transient batch would
// need pacing or a durable queue, both intentionally outside one-shot upload.
export const MEDIA_UPLOAD_QUEUE_MAX_ITEMS = 5;

const VIDEO_EXTENSIONS = ['.mp4', '.avi', '.mkv', '.mov', '.webm'];
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.flac', '.m4a', '.ogg', '.aac'];
const IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.gif',
  '.bmp',
  '.tiff',
  '.heic',
  '.heif',
];

const VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/avi',
  'video/msvideo',
  'video/x-msvideo',
  'video/mkv',
  'video/x-matroska',
  'video/quicktime',
  'video/webm',
];

const AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'audio/flac',
  'audio/x-flac',
  'audio/m4a',
  'audio/mp4',
  'audio/x-m4a',
  'audio/ogg',
  'audio/aac',
  'audio/x-aac',
];

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'image/heic',
  'image/heif',
];

const extensionMatches = (fileName: string, extensions: readonly string[]): boolean => {
  const normalized = fileName.trim().toLowerCase();
  return extensions.some((extension) => normalized.endsWith(extension));
};

const classifyByExtension = (fileName: string): UploadMediaType => {
  if (extensionMatches(fileName, AUDIO_EXTENSIONS)) return 'audio';
  if (extensionMatches(fileName, IMAGE_EXTENSIONS)) return 'image';
  if (extensionMatches(fileName, VIDEO_EXTENSIONS)) return 'video';
  return 'unknown';
};

const classifyByMimeType = (mimeType: string): UploadMediaType => {
  if (AUDIO_MIME_TYPES.includes(mimeType)) return 'audio';
  if (IMAGE_MIME_TYPES.includes(mimeType)) return 'image';
  if (VIDEO_MIME_TYPES.includes(mimeType)) return 'video';
  return 'unknown';
};

export const classifyUploadMedia = (
  fileName: string,
  mimeType?: string | null,
): UploadMediaType => {
  const normalizedMime = String(mimeType || '').trim().toLowerCase();
  const extensionType = classifyByExtension(fileName);
  if (!normalizedMime) return extensionType;
  if (normalizedMime === 'application/octet-stream' || normalizedMime === 'binary/octet-stream') {
    return extensionType;
  }
  const mimeMediaType = classifyByMimeType(normalizedMime);
  if (mimeMediaType === 'unknown') return 'unknown';
  return extensionType !== 'unknown' && extensionType !== mimeMediaType ? 'unknown' : mimeMediaType;
};

/** Mirrors the backend upload validator's finite MIME and extension allow-lists. */
export const isSupportedLocalMediaFile = (file: Pick<File, 'name' | 'type'>): boolean => (
  classifyUploadMedia(file.name, file.type) !== 'unknown'
);

/** Picker MIME filters are exact because Google Drive does not support wildcard MIME filters. */
export const GOOGLE_DRIVE_PICKER_MIME_TYPES = [
  ...VIDEO_MIME_TYPES,
  ...AUDIO_MIME_TYPES,
  ...IMAGE_MIME_TYPES,
].join(',');

export const isSupportedGoogleDriveMedia = (
  fileName: string,
  mimeType?: string | null,
): boolean => {
  const normalizedMime = String(mimeType || '').trim().toLowerCase();
  return (
    VIDEO_MIME_TYPES.includes(normalizedMime)
    || AUDIO_MIME_TYPES.includes(normalizedMime)
    || IMAGE_MIME_TYPES.includes(normalizedMime)
  ) && classifyUploadMedia(fileName, normalizedMime) !== 'unknown';
};

export const LOCAL_VIDEO_FILE_ACCEPT = [...VIDEO_MIME_TYPES, ...VIDEO_EXTENSIONS].join(',');

export const LOCAL_MEDIA_FILE_ACCEPT = [
  ...VIDEO_MIME_TYPES,
  ...AUDIO_MIME_TYPES,
  ...IMAGE_MIME_TYPES,
  ...VIDEO_EXTENSIONS,
  ...AUDIO_EXTENSIONS,
  ...IMAGE_EXTENSIONS,
].join(',');

export const SUPPORTED_MEDIA_FORMAT_LABEL = [
  ...VIDEO_EXTENSIONS,
  ...AUDIO_EXTENSIONS,
  ...IMAGE_EXTENSIONS,
].map((extension) => extension.slice(1).toUpperCase()).join(', ');
